import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Lock, AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

/**
 * Private announcement view — accessed via /a/private/:token
 * Validates the token, increments view count, then redirects to
 * the actual announcement page with a validated query param.
 */
export default function PrivateAnnouncementView() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'expired' | 'view_limit' | 'invalid' | 'success'>('loading');

  useEffect(() => {
    if (!token) {
      setStatus('invalid');
      return;
    }

    const validateAndRedirect = async () => {
      // Look up the token (via security definer function - no broad SELECT policy)
      const { data: linkResult, error } = await supabase
        .rpc('get_share_link_by_token', { p_token: token } as any);

      const linkData = Array.isArray(linkResult) ? linkResult[0] : linkResult;

      if (error || !linkData) {
        setStatus('invalid');
        return;
      }

      // Check expiration
      if (linkData.expires_at && new Date(linkData.expires_at) < new Date()) {
        setStatus('expired');
        return;
      }

      // Check view limit
      if (linkData.max_views !== null && linkData.view_count >= linkData.max_views) {
        setStatus('view_limit');
        return;
      }

      // Increment view count (via security definer function)
      await supabase.rpc('increment_share_link_view', { p_token: token } as any);

      // Get the announcement's public slug
      const { data: announcement } = await supabase
        .from('announcements')
        .select('public_slug')
        .eq('id', linkData.announcement_id)
        .maybeSingle();

      if (!announcement?.public_slug) {
        // If no public slug, the announcement might be a draft — show it anyway via direct view
        // Store token in sessionStorage for this session
        sessionStorage.setItem('private_access_token', token);
        setStatus('success');
        // Navigate to a special private view
        navigate(`/announcement/${linkData.announcement_id}?private_token=${token}`, { replace: true });
        return;
      }

      // Store validation in session and redirect to public view
      sessionStorage.setItem('private_access_token', token);
      setStatus('success');
      navigate(`/a/${announcement.public_slug}`, { replace: true });
    };

    validateAndRedirect();
  }, [token, navigate]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <motion.div
          animate={{ scale: [1, 1.2, 1] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="flex flex-col items-center gap-4"
        >
          <Lock className="h-12 w-12 text-primary" />
          <p className="text-sm text-muted-foreground">Verifying private link...</p>
        </motion.div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-hero-gradient">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="flex flex-col items-center gap-4"
        >
          <Heart className="h-12 w-12 text-primary" />
          <p className="text-sm text-muted-foreground">Redirecting to your message...</p>
        </motion.div>
      </div>
    );
  }

  const errorConfig = {
    expired: {
      icon: <AlertTriangle className="h-14 w-14 text-primary" />,
      title: 'This link has expired',
      description: 'The person who shared this set a time limit. Ask them for a new link.',
    },
    view_limit: {
      icon: <AlertTriangle className="h-14 w-14 text-primary" />,
      title: 'View limit reached',
      description: 'This private link has reached its maximum number of views.',
    },
    invalid: {
      icon: <Lock className="h-14 w-14 text-muted-foreground/50" />,
      title: 'Invalid or deactivated link',
      description: 'This private link is no longer valid. It may have been revoked by the sender.',
    },
  };

  const config = errorConfig[status];

  return (
    <div className="min-h-screen flex items-center justify-center bg-hero-gradient p-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center max-w-sm mx-auto space-y-6"
      >
        <div className="flex justify-center">{config.icon}</div>
        <div className="space-y-2">
          <h1 className="text-xl font-bold text-foreground">{config.title}</h1>
          <p className="text-sm text-muted-foreground">{config.description}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
          <Heart className="h-4 w-4" />
          Go to Say It NOW
        </Button>
      </motion.div>
    </div>
  );
}
